import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
    LayoutDashboard, ContactRound, Calendar, ClipboardList, BarChart3,
    CreditCard, TrendingUp, Home, KeyRound, ShieldCheck,
    SlidersHorizontal, LifeBuoy, ExternalLink,
    ChevronLeft, ChevronRight, X,
} from 'lucide-react';
import { homeAppUrl } from '@goodsteward/gs-ui';
import { useAuthStore, type AuthUser } from '@/stores/auth';
import { cn } from '@/lib/cn';
import { Logo } from '@/components/ui/Logo';

type NavItem = {
    label: string;
    to: string;
    icon: React.ElementType;
    visible: (u: AuthUser) => boolean;
    external?: boolean;
};
type NavGroup = { label: string; items: NavItem[] };

const hasAny = (u: AuthUser, ...perms: string[]) =>
    perms.some((p) => u.permissions.includes(p));
const isParent = (u: AuthUser) => u.roleType === 'parent';

const CLIENT_IMPLIED = [
    'view_all_clients', 'view_assigned_clients',
    'view_scheduling', 'edit_scheduling',
    'view_notes', 'write_notes', 'finalize_notes', 'data_collection',
    'view_treatment_plans', 'edit_treatment_plans',
    'view_billing', 'edit_billing',
    'view_audit_log', 'manage_settings',
];

const GROUPS: NavGroup[] = [
    {
        label: 'Overview',
        items: [
            { label: 'Dashboard', to: '/', icon: LayoutDashboard, visible: (u) => !isParent(u) },
        ],
    },
    {
        label: 'Clinical',
        items: [
            { label: 'Clients',         to: '/clients',         icon: ContactRound,  visible: (u) => !isParent(u) && hasAny(u, ...CLIENT_IMPLIED) },
            { label: 'Scheduling',      to: '/scheduling',      icon: Calendar,      visible: (u) => !isParent(u) && hasAny(u, 'view_scheduling', 'edit_scheduling') },
            { label: 'Treatment Plans', to: '/treatment-plans', icon: ClipboardList, visible: (u) => !isParent(u) && hasAny(u, 'view_treatment_plans', 'edit_treatment_plans') },
            { label: 'Data Analysis',   to: '/data-analysis',   icon: BarChart3,     visible: (u) => !isParent(u) && hasAny(u, 'view_reports', 'export_reports') },
        ],
    },
    {
        label: 'Operations',
        items: [
            { label: 'Billing',   to: '/billing',   icon: CreditCard, visible: (u) => !isParent(u) && hasAny(u, 'view_billing', 'edit_billing', 'submit_claims') },
            { label: 'Reporting', to: '/reporting', icon: TrendingUp, visible: (u) => !isParent(u) && hasAny(u, 'view_reports', 'export_reports') },
        ],
    },
    {
        label: 'Communications',
        items: [
            { label: 'Parent Portal', to: '/portal', icon: Home, visible: (u) => hasAny(u, 'view_portal') || isParent(u) },
        ],
    },
    {
        label: 'Admin',
        items: [
            { label: 'Audit Log',      to: '/audit-log',     icon: ShieldCheck,        visible: (u) => !isParent(u) && hasAny(u, 'view_audit_log') },
            { label: 'Configuration',  to: '/configuration', icon: SlidersHorizontal,  visible: (u) => !isParent(u) && hasAny(u, 'manage_settings') },
            { label: 'Manage practice users', to: homeAppUrl('pma'), icon: KeyRound, external: true, visible: (u) => !isParent(u) },
        ],
    },
    {
        label: 'Help',
        items: [
            { label: 'Support Center', to: 'https://support.goodstewardaba.com/pma/', icon: LifeBuoy, external: true, visible: () => true },
        ],
    },
];

const COLLAPSED_KEY = 'gs.sidebar.collapsed';

interface SidebarProps {
    /** Below `md:`, the sidebar renders as an off-canvas drawer toggled by
     *  this flag. Above `md:`, it's always in flow and the flag is ignored. */
    mobileOpen: boolean;
    onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
    const user = useAuthStore((s) => s.user);
    const location = useLocation();
    const [collapsed, setCollapsed] = useState<boolean>(
        () => typeof window !== 'undefined' && localStorage.getItem(COLLAPSED_KEY) === '1',
    );

    useEffect(() => {
        localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    }, [collapsed]);

    // Close the mobile drawer on every route change so a tap on a nav item
    // returns the user to a clean main view. Desktop is unaffected — the
    // mobileOpen flag is always false there.
    useEffect(() => {
        if (mobileOpen) onMobileClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // ESC closes the drawer when it's open.
    useEffect(() => {
        if (!mobileOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onMobileClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mobileOpen, onMobileClose]);

    if (!user) return null;

    return (
        <>
            {/* Backdrop — only meaningful on mobile. Pointer-events off when
                the drawer is closed so it doesn't intercept taps. */}
            <div
                aria-hidden
                onClick={onMobileClose}
                className={cn(
                    'fixed inset-0 z-40 bg-black/50 md:hidden transition-opacity duration-150',
                    mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
                )}
            />
            <nav
                aria-label="Main navigation"
                aria-hidden={!mobileOpen ? undefined : false}
                className={cn(
                    'flex flex-col bg-sidebar text-sidebar-text shrink-0 h-screen',
                    // Mobile: off-canvas drawer.
                    'fixed inset-y-0 left-0 z-50 w-[var(--size-sidebar)]',
                    'transition-transform duration-150 ease-[cubic-bezier(0.4,0,0.2,1)]',
                    mobileOpen ? 'translate-x-0' : '-translate-x-full',
                    // Desktop: sticky in flow, no transform, width animates on collapse.
                    'md:sticky md:top-0 md:translate-x-0 md:z-auto md:transition-[width]',
                    collapsed ? 'md:w-[var(--size-sidebar-collapsed)]' : 'md:w-[var(--size-sidebar)]',
                )}
            >
            <div
                className={cn(
                    'h-[var(--size-topbar)] flex items-center border-b border-white/10 shrink-0 overflow-hidden',
                    collapsed ? 'md:justify-center md:px-2' : 'justify-start px-3',
                )}
            >
                {collapsed ? (
                    <Logo variant="mark" tone="white" className="h-8 w-8 hidden md:block" />
                ) : null}
                <Logo
                    variant="full"
                    tone="white"
                    size="sm"
                    className={cn(collapsed && 'md:hidden')}
                />
                {/* Close affordance for the mobile drawer. Desktop hides this —
                    desktop has the Collapse button at the bottom. */}
                <button
                    type="button"
                    onClick={onMobileClose}
                    aria-label="Close navigation"
                    className="ml-auto p-1.5 rounded-md text-sidebar-text hover:bg-sidebar-hover md:hidden"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden py-3">
                {GROUPS.map((group) => {
                    const visibleItems = group.items.filter((item) => item.visible(user));
                    if (!visibleItems.length) return null;
                    return (
                        <div key={group.label} className="mb-2">
                            <div className={cn(
                                'px-4 mb-1 text-[10px] font-bold uppercase tracking-wider text-sidebar-text-subtle',
                                // On mobile the drawer always shows full content;
                                // collapse only hides labels on desktop.
                                collapsed && 'md:hidden',
                            )}>
                                {group.label}
                            </div>
                            {visibleItems.map((item) => {
                                const Icon = item.icon;
                                const active = !item.external && (item.to === '/'
                                    ? location.pathname === '/'
                                    : location.pathname === item.to || location.pathname.startsWith(item.to + '/'));
                                const className = cn(
                                    'flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm transition-colors',
                                    active
                                        ? 'bg-accent text-[#111] font-medium'
                                        : 'text-sidebar-text hover:bg-sidebar-hover',
                                    collapsed && 'md:justify-center',
                                );
                                if (item.external) {
                                    return (
                                        <a
                                            key={item.to}
                                            href={item.to}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={collapsed ? item.label : undefined}
                                            className={className}
                                        >
                                            <Icon className="w-4 h-4 shrink-0" />
                                            <span className={cn('truncate flex-1', collapsed && 'md:hidden')}>{item.label}</span>
                                            <ExternalLink className={cn('w-3 h-3 shrink-0 opacity-60', collapsed && 'md:hidden')} aria-hidden />
                                        </a>
                                    );
                                }
                                return (
                                    <Link
                                        key={item.to}
                                        to={item.to}
                                        title={collapsed ? item.label : undefined}
                                        className={className}
                                    >
                                        <Icon className="w-4 h-4 shrink-0" />
                                        <span className={cn('truncate', collapsed && 'md:hidden')}>{item.label}</span>
                                    </Link>
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            {/* Collapse toggle — desktop only. Mobile uses the X button up
                top to dismiss the drawer; "collapse" doesn't make sense for
                an off-canvas overlay. */}
            <div className="border-t border-white/10 p-2 shrink-0 hidden md:block">
                <button
                    onClick={() => setCollapsed((c) => !c)}
                    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-sidebar-text hover:bg-sidebar-hover transition-colors',
                        collapsed && 'justify-center',
                    )}
                >
                    {collapsed
                        ? <ChevronRight className="w-4 h-4 shrink-0" />
                        : <ChevronLeft className="w-4 h-4 shrink-0" />}
                    {!collapsed && <span>Collapse</span>}
                </button>
            </div>
            </nav>
        </>
    );
}
